import {
    IF,
    IS,
    IS_NOT,
    nil,
    NO,
    UIButton,
    UIColor,
    UIRoute,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    UIViewController,
    YES
} from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import "../Core/RHCoreInitializer"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHTextField } from "../Custom components/RH/RHTextField"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHLoginViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementSearchViewController } from "../Search/ProcurementSearchViewController"
import { StatisticsMainViewController } from "../StatisticsMainViewController"
import { ForgotPasswordViewController } from "./ForgotPasswordViewController"
import { GoogleLoginButton } from "./GoogleLoginButton"
import { MicrosoftLoginButton } from "./MicrosoftLoginButton"
import { RegisterUserViewController } from "./RegisterUserViewController"


type AuthenticationChallengeKind = "totp-login" | "totp-enrollment"

interface AuthenticationChallengeResponse {
    isActive: boolean
    kind?: AuthenticationChallengeKind
    email?: string
    secret?: string
    otpauthURL?: string
}

interface AuthenticationLoginResponse {
    isMfaRequired?: boolean
    reason?: string
}


export class LoginViewController extends UIViewController {

    readonly welcomeHeadingLabel: UITextView = new UITextView(
        this.view.elementID + "WelcomeHeadingLabel",
        UITextView.type.header2
    ).configuredWithObject({
        isSingleLine: NO,
        text: "Tere tulemast hankekeskkonda",
        textColor: new UIColor(RHColors.textPrimary.stringValue),
        textAlignment: UITextView.textAlignment.center,
        backgroundColor: UIColor.transparentColor,
        style: {
            fontFamily: RHFont.familyBase,
            textAlign: "center",
            lineHeight: RHFont.lineHeightHeading,
            fontSize: RHLoginViewTokens.welcomeHeadingFontSize,
            fontWeight: RHLoginViewTokens.welcomeHeadingFontWeight,
            color: RHColors.textPrimary.stringValue,
            margin: "0",
            padding: "0"
        }
    }).addedAsSubviewToView(this.view)

    readonly titleLabel: UITextView = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.paragraph
    ).configuredWithObject({
        isSingleLine: NO,
        text: `Siin saad korraldada hankeid, mille läbiviimiseks ei ole vaja kasutada <a href="https://riigihanked.riik.ee/rhr-web/#/" target="_blank" style="color: ${RHColors.primaryBlue.stringValue}; text-decoration: underline; font-weight: ${"500"};"><b>Riigihangete registrit</b></a>.`,
        textColor: new UIColor(RHColors.textSecondary.stringValue),
        textAlignment: UITextView.textAlignment.center,
        style: {
            fontFamily: RHFont.familyBase,
            textAlign: "center",
            lineHeight: RHLoginViewTokens.introLineHeight,
            fontSize: RHFont.sizeMedium,
            fontWeight: RHLoginViewTokens.introFontWeight,
            margin: "0",
            padding: "0"
        }
    }).addedAsSubviewToView(this.view)
    
    explanationView: UITextView = new UITextView(this.view.elementID + "ExplanationView").addedAsSubviewToView(
        this.view).configuredWithObject({
        isSingleLine: NO,
        text: "Keskkond võimaldab hankijal korraldada hanke elektrooniliselt ja läbipaistvalt alates ettevõtjate kutsumisest kuni pakkumuste esitamise, avamise, hindamise ja tulemuse vormistamiseni.<br><br>Pakkujad saavad tutvuda neile suunatud ja avalike hangetega ning esitada pakkumusi turvaliselt otse keskkonnas.<br><br>Keskkonna eesmärk on muuta hangete korraldamine lihtsamaks ja vähendada hankijate ning pakkujate halduskoormust, säilitades samal ajal hankeprotsessi läbipaistvuse ja jälgitavuse.<br><br>Hankija vastutab sobiva menetluse valimise ja oma hankele kohalduvate õigusaktide ning sisemiste reeglite järgimise eest.",
        textColor: new UIColor(RHColors.textSecondary.stringValue),
        textAlignment: UITextView.textAlignment.center,
        style: {
            fontFamily: RHFont.familyBase,
            textAlign: "center",
            lineHeight: RHLoginViewTokens.introLineHeight,
            fontSize: RHFont.sizeMedium,
            fontWeight: RHLoginViewTokens.introFontWeight,
            margin: "0",
            padding: "0"
        }
    })

    searchField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "Otsi keskkonnas tehtud hankeid",
        controlEventTargetAccumulator: {
            EnterDown: () => {
                this._performProcurementSearch()
            }
        }
    })
    searchButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Otsi" },
        variant: "success",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._performProcurementSearch()
            }
        }
    })
    browseProcurementsButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Sirvi kõiki hankeid" },
        variant: "link",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._browseProcurements()
            }
        }
    })

    statusBannerView = new RHAlertBannerView({
        textAlignment: UITextView.textAlignment.center
    }).addedAsSubviewToView(this.view)

    mfaTitleLabel = new UITextView(
        this.view.elementID + "MFATitleLabel",
        UITextView.type.header4
    ).addedAsSubviewToView(this.view).configuredWithObject({
        isSingleLine: NO,
        text: "",
        textColor: new UIColor(RHColors.textPrimary.stringValue),
        textAlignment: UITextView.textAlignment.center,
        backgroundColor: UIColor.transparentColor,
        style: {
            fontFamily: RHFont.familyBase,
            lineHeight: RHFont.lineHeightHeading,
            fontSize: RHFont.sizeLarge,
            fontWeight: "600"
        }
    })

    mfaInstructionLabel = new UITextView(
        this.view.elementID + "MFAInstructionLabel",
        UITextView.type.paragraph
    ).addedAsSubviewToView(this.view).configuredWithObject({
        isSingleLine: NO,
        text: "",
        textColor: new UIColor(RHColors.textSecondary.stringValue),
        textAlignment: UITextView.textAlignment.center,
        backgroundColor: UIColor.transparentColor,
        style: {
            fontFamily: RHFont.familyBase,
            lineHeight: RHLoginViewTokens.introLineHeight,
            fontSize: RHFont.sizeMedium,
            fontWeight: RHLoginViewTokens.introFontWeight
        }
    })

    mfaSecretLabel = new UITextView(
        this.view.elementID + "MFASecretLabel",
        UITextView.type.paragraph
    ).addedAsSubviewToView(this.view).configuredWithObject({
        isSingleLine: NO,
        text: "",
        textColor: new UIColor(RHColors.textPrimary.stringValue),
        textAlignment: UITextView.textAlignment.center,
        backgroundColor: UIColor.transparentColor,
        style: {
            fontFamily: "monospace",
            lineHeight: RHLoginViewTokens.introLineHeight,
            fontSize: RHFont.sizeMedium,
            fontWeight: "600",
            wordBreak: "break-word"
        }
    })

    mfaCodeTextField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "Sisesta 6-kohaline kood",
        controlEventTargetAccumulator: {
            EnterDown: async () => {
                await this._verifyAuthenticationChallenge()
            }
        }
    })

    verifyMfaButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Kinnita" },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async () => {
                await this._verifyAuthenticationChallenge()
            }
        }
    })

    cancelMfaButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Tühista" },
        variant: "link",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async () => {
                await this._cancelAuthenticationChallenge()
            }
        }
    })
    
    usernameTextField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "Sisesta e-posti aadress",
        controlEventTargetAccumulator: {
            EnterDown: (sender, event) => {
                if (IS(this.usernameTextField.text?.length) && IS(this.passwordTextField.text?.length)) {
                    this.loginButton.sendControlEventForKey(UIButton.controlEvent.EnterDown, nil)
                }
                else {
                    this.passwordTextField.focus()
                }
            }
        }
        
    })
    passwordTextField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({
        placeholderText: "Salasõna",
        type: "password",
        controlEventTargetAccumulator: {
            EnterDown: (sender, event) => {
                this.loginButton.sendControlEventForKey(UIButton.controlEvent.EnterDown, nil)
            }
        }
    })
    
    forgotPasswordButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Unustasid salasõna?" },
        variant: "link",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(LoginViewController.routeComponentName)
                    .routeWithViewControllerComponent(ForgotPasswordViewController, {})
                    .apply()
            }
        }
    })
    
    loginButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Logi sisse"
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: async (sender, event) => {
                
                try {
                    
                    this.view.loading = YES
                    
                    const result = await fetch("/auth/login", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            email: this.usernameTextField.text,
                            password: this.passwordTextField.text
                        })
                    })
                    const body = await result.json().catch(() => ({})) as AuthenticationLoginResponse
                    
                    if (result.ok && IS(body.isMfaRequired)) {
                        await this._loadAuthenticationChallenge()
                    }
                    else if (result.ok) {
                        RHCore.sharedInstance.reloadSocketConnection()
                    }
                    else {
                        
                        this.view.loading = NO
                        
                        if (result.status === 403 && body.reason === "emailNotConfirmed") {
                            this.statusBannerView.configureWithObject({
                                text: "E-posti aadress ei ole kinnitatud. Palun kontrolli oma postkasti.",
                                alertType: RHAlertBannerView.type.Warning
                            })
                            this.view.setNeedsLayoutUpToRootView()
                        }
                        else if (body.reason === "invalidCredentials") {
                            CBDialogViewShower.alert("Sisselogimine ebaõnnestus: vale e-posti aadress või salasõna.")
                        }
                        else {
                            CBDialogViewShower.alert("Sisselogimine ebaõnnestus.")
                        }
                        
                    }
                    
                }
                catch (error) {
                    this.view.loading = NO
                    CBDialogViewShower.alert(
                        "Ühenduse viga. Palun kontrolli internetiühendust ja proovi uuesti.")
                    
                }
                
            }
        }
    })
    
    googleLoginButton = new GoogleLoginButton().addedAsSubviewToView(this.view).configuredWithObject({
        // titleLabel: { text: "Logi sisse Google'iga" },
        // variant: "secondary",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                window.location.href = "/auth/google"
            }
        }
    })
    
    microsoftLoginButton = new MicrosoftLoginButton().addedAsSubviewToView(this.view).configuredWithObject({
        // titleLabel: { text: "Logi sisse Microsoftiga" },
        // variant: "secondary",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                window.location.href = "/auth/microsoft"
            }
        }
    })
    
    // Added to the view only when the server has organization Entra logins configured (see the constructor).
    organizationLoginButton = new MicrosoftLoginButton().configuredWithObject({
        titleLabel: { text: "Logi sisse oma organisatsiooni kontoga" },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._beginOrganizationLoginForEnteredEmail()
            }
        }
    })
    
    registerUserButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Registreeri"
        },
        variant: "success",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                
                UIRoute.currentRoute.routeByRemovingComponentNamed(LoginViewController.routeComponentName)
                    .routeWithViewControllerComponent(RegisterUserViewController, nil)
                    .apply()
                
            }
        }
    })
    // Public statistics is no longer the default landing route, so the only way an
    // unauthenticated visitor can still reach the public statistics page is this link
    // (the top bar's statistics button is shown only when logged in).
    statisticsLink = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: { text: "Vaata riigihangete avalikku statistikat" },
        variant: "link",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(LoginViewController.routeComponentName)
                    .routeWithViewControllerComponent(StatisticsMainViewController, {})
                    .apply()
            }
        }
    })
    bottomView = new UIView().addedAsSubviewToView(this.view)

    _authenticationChallengeKind?: AuthenticationChallengeKind

    override _focusManager = new RHFocusManager(() => {
        const publicFocusItems = [
            this.searchField,
            this.searchButton,
            this.browseProcurementsButton
        ]

        if (this._authenticationChallengeKind) {
            return [
                ...publicFocusItems,
                this.mfaCodeTextField,
                this.verifyMfaButton,
                this.cancelMfaButton,
                this.statisticsLink
            ]
        }

        return [
            ...publicFocusItems,
            this.usernameTextField,
            this.passwordTextField,
            this.forgotPasswordButton,
            this.loginButton,
            this.registerUserButton,
            this.googleLoginButton,
            this.microsoftLoginButton,
            ...IF(this.view.hasSubview(this.organizationLoginButton))(() =>
                [this.organizationLoginButton]
            ).ELSE(() =>
                []
            ),
            this.statisticsLink
        ]
    })
    
    
    constructor(view: UIView) {
        
        super(view)
        
        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor
        })
        if (window.CBCoreInitializerObject?.isEntraOrganizationLoginAvailable) {
            this.organizationLoginButton.addedAsSubviewToView(this.view)
        }
        UIView.injectCSS(
            `
            #${this.titleLabel.elementID}_textElement,
            #${this.explanationView.elementID}_textElement {
                color: ${RHColors.textSecondary.stringValue} !important;
                font-family: ${RHFont.familyBase} !important;
                font-size: ${RHFont.sizeMedium} !important;
                font-weight: ${RHLoginViewTokens.introFontWeight} !important;
                line-height: ${RHLoginViewTokens.introLineHeight} !important;
                text-align: center !important;
            }
            `,
            "rh-login-intro-typography"
        )
        UIView.injectCSS(
            `
            #${this.welcomeHeadingLabel.elementID}_textElement {
                color: ${RHColors.textPrimary.stringValue} !important;
                font-family: ${RHFont.familyBase} !important;
                font-size: ${RHLoginViewTokens.welcomeHeadingFontSize} !important;
                font-weight: ${RHLoginViewTokens.welcomeHeadingFontWeight} !important;
                line-height: ${RHFont.lineHeightHeading} !important;
                text-align: center !important;
            }
            `,
            "rh-login-welcome-heading-typography"
        )
        this._updateAuthenticationChallengeViews()

    }
    
    
    static override readonly routeComponentName = "login"
    
    static override readonly ParameterIdentifierName = {
        email: "email",
        emailConfirmed: "emailConfirmed",
        invitationAccepted: "invitationAccepted",
        passwordResetSuccess: "passwordResetSuccess",
        oauthError: "oauthError",
        entraOrganizationNotFound: "entraOrganizationNotFound",
        entraOrganizationLoginRequired: "entraOrganizationLoginRequired",
        mfaChallenge: "mfaChallenge"
    }
    
    _beginOrganizationLoginForEnteredEmail() {
        const email = (this.usernameTextField.text ?? "").trim()
        if (IS_NOT(email.length)) {
            this.statusBannerView.configureWithObject({
                text: "Sisesta oma töö e-posti aadress ja vajuta uuesti „Logi sisse oma organisatsiooni kontoga“.",
                alertType: RHAlertBannerView.type.Error
            })
            this.view.setNeedsLayoutUpToRootView()
            return
        }
        window.location.href = "/auth/entra?email=" + encodeURIComponent(email)
    }
    
    _performProcurementSearch() {
        const searchText = (this.searchField.text ?? "").trim()
        let routeParameters: Record<string, string> = {}
        if (searchText.length > 0) {
            routeParameters = {
                [ProcurementSearchViewController.ParameterIdentifierName.searchText]: searchText
            }
        }

        UIRoute.currentRoute
            .routeByRemovingComponentNamed(LoginViewController.routeComponentName)
            .routeWithViewControllerComponent(ProcurementSearchViewController, routeParameters)
            .apply()
    }


    _browseProcurements() {
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(LoginViewController.routeComponentName)
            .routeWithViewControllerComponent(ProcurementSearchViewController, {})
            .apply()
    }


    async _loadAuthenticationChallenge() {

        try {
            this.view.loading = YES

            const response = await fetch("/auth/mfa/challenge", {
                method: "GET",
                headers: { "Accept": "application/json" }
            })
            const challenge = await response.json().catch(() => ({})) as AuthenticationChallengeResponse

            if (!response.ok || !IS(challenge.isActive) || !challenge.kind) {
                this._clearAuthenticationChallenge()
                CBDialogViewShower.alert("Sisselogimise kinnitamine aegus. Palun logige uuesti sisse.")
                return
            }

            this._displayAuthenticationChallenge(challenge)

        }
        catch (error) {
            this._clearAuthenticationChallenge()
            CBDialogViewShower.alert("Ühenduse viga. Palun kontrollige internetiühendust ja proovige uuesti.")
        }
        finally {
            this.view.loading = NO
        }

    }


    _displayAuthenticationChallenge(challenge: AuthenticationChallengeResponse) {

        this._authenticationChallengeKind = challenge.kind
        this.mfaCodeTextField.text = nil

        if (challenge.kind === "totp-enrollment") {
            this.mfaTitleLabel.text = "Seadista autentimisrakendus"
            this.mfaInstructionLabel.text =
                "Enne sisselogimise lõpetamist lisa see konto autentimisrakendusse ja sisesta rakenduse 6-kohaline kood."
            if (challenge.secret) {
                this.mfaSecretLabel.text =
                    `Seadistamise võti: ${this._formattedAuthenticationSecret(challenge.secret)}`
            }
            else {
                this.mfaSecretLabel.text = ""
            }
        }
        else {
            this.mfaTitleLabel.text = "Kinnita sisselogimine"
            this.mfaInstructionLabel.text =
                "Sisesta autentimisrakenduses kuvatav 6-kohaline kood."
            this.mfaSecretLabel.text = ""
        }

        this._updateAuthenticationChallengeViews()
        this.mfaCodeTextField.focus()
        this.view.setNeedsLayoutUpToRootView()

    }


    _clearAuthenticationChallenge() {

        this._authenticationChallengeKind = nil
        this.mfaCodeTextField.text = nil
        this.mfaTitleLabel.text = ""
        this.mfaInstructionLabel.text = ""
        this.mfaSecretLabel.text = ""
        this._updateAuthenticationChallengeViews()
        this.view.setNeedsLayoutUpToRootView()

    }


    async _verifyAuthenticationChallenge() {

        const code = (this.mfaCodeTextField.text ?? "").replace(/\s/g, "")
        if (!/^\d{6}$/.test(code)) {
            CBDialogViewShower.alert("Sisestage autentimisrakenduse 6-kohaline kood.")
            return
        }

        try {
            this.view.loading = YES
            const response = await fetch("/auth/mfa/totp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code })
            })

            if (response.ok) {
                RHCore.sharedInstance.reloadSocketConnection()
                return
            }

            this.view.loading = NO
            CBDialogViewShower.alert("Kood ei sobi või on aegunud. Proovige uuesti.")
        }
        catch (error) {
            this.view.loading = NO
            CBDialogViewShower.alert("Ühenduse viga. Palun kontrollige internetiühendust ja proovige uuesti.")
        }

    }


    async _cancelAuthenticationChallenge() {

        try {
            await fetch("/auth/mfa/cancel", { method: "POST" })
        }
        catch (error) {
            console.error("MFA challenge cancellation error:", error)
        }

        this._clearAuthenticationChallenge()
        UIRoute.currentRoute
            .routeByRemovingParameterInComponent(
                LoginViewController.routeComponentName,
                LoginViewController.ParameterIdentifierName.mfaChallenge
            )
            .applyByReplacingCurrentRouteInHistory()

    }


    _updateAuthenticationChallengeViews() {

        if (this._authenticationChallengeKind) {
            this.usernameTextField.hidden = YES
            this.passwordTextField.hidden = YES
            this.forgotPasswordButton.hidden = YES
            this.loginButton.hidden = YES
            this.registerUserButton.hidden = YES
            this.googleLoginButton.hidden = YES
            this.microsoftLoginButton.hidden = YES
            this.organizationLoginButton.hidden = YES
            this.mfaTitleLabel.hidden = NO
            this.mfaInstructionLabel.hidden = NO
            this.mfaSecretLabel.hidden = NO
            this.mfaCodeTextField.hidden = NO
            this.verifyMfaButton.hidden = NO
            this.cancelMfaButton.hidden = NO
            return
        }

        this.usernameTextField.hidden = NO
        this.passwordTextField.hidden = NO
        this.forgotPasswordButton.hidden = NO
        this.loginButton.hidden = NO
        this.registerUserButton.hidden = NO
        this.googleLoginButton.hidden = NO
        this.microsoftLoginButton.hidden = NO
        this.organizationLoginButton.hidden = NO
        this.mfaTitleLabel.hidden = YES
        this.mfaInstructionLabel.hidden = YES
        this.mfaSecretLabel.hidden = YES
        this.mfaCodeTextField.hidden = YES
        this.verifyMfaButton.hidden = YES
        this.cancelMfaButton.hidden = YES

    }


    _formattedAuthenticationSecret(secret: string) {

        const secretGroups = secret.match(/.{1,4}/g)
        if (!secretGroups) {
            return secret
        }

        return secretGroups.join(" ")

    }


    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.userDidLogIn) {
            this.view.loading = NO
        }
    }


    override async viewWillAppear() {

        await super.viewWillAppear()

        RHPageFocusManager.shared.registerContentControllerManager(this._focusManager)

    }
    
    override async viewDidAppear() {
        
        await super.viewDidAppear()

        if (this._authenticationChallengeKind) {
            this.mfaCodeTextField.focus()
        }
        else {
            this.usernameTextField.focus()
        }
        
    }
    
    
    override async viewWillDisappear() {
        
        await super.viewWillDisappear()
        
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager)
        
    }
    
    
    override async handleRoute(route: UIRoute) {
        
        super.handleRoute(route)
        const loginComponent = route.componentWithName(LoginViewController.routeComponentName)
        
        
        if (loginComponent?.parameters.email) {
            this.usernameTextField.text = loginComponent?.parameters.email
            this.passwordTextField.text = nil
            this.passwordTextField.focus()
        }
        
        if (loginComponent?.parameters.emailConfirmed === "YES") {
            this.statusBannerView.configureWithObject({
                text: "E-posti aadress on kinnitatud. Saad nüüd sisse logida.",
                alertType: RHAlertBannerView.type.Success
            })
            this.view.setNeedsLayoutUpToRootView()
        }
        
        if (loginComponent?.parameters[LoginViewController.ParameterIdentifierName.passwordResetSuccess] === "YES") {
            this.statusBannerView.configureWithObject({
                text: "Salasõna on muudetud. Saad nüüd sisse logida.",
                alertType: RHAlertBannerView.type.Success
            })
            this.view.setNeedsLayoutUpToRootView()
        }

        if (loginComponent?.parameters[LoginViewController.ParameterIdentifierName.invitationAccepted] === "YES") {
            this.statusBannerView.configureWithObject({
                text: "Kutse on vastu võetud ja konto on ettevõttega seotud. Saad nüüd sisse logida.",
                alertType: RHAlertBannerView.type.Success
            })
            this.view.setNeedsLayoutUpToRootView()
        }
        
        if (loginComponent?.parameters[LoginViewController.ParameterIdentifierName.oauthError] === "YES") {
            this.statusBannerView.configureWithObject({
                text: "Sisselogimine ebaõnnestus. Palun proovi uuesti või kasuta e-posti aadressi ja salasõna.",
                alertType: RHAlertBannerView.type.Error
            })
            this.view.setNeedsLayoutUpToRootView()
        }

        if (loginComponent?.parameters[LoginViewController.ParameterIdentifierName.entraOrganizationNotFound] === "YES") {
            this.statusBannerView.configureWithObject({
                text: "Selle e-posti aadressi organisatsioon ei kasuta RHR-is oma sisselogimist. " +
                    "Logi sisse e-posti aadressi ja salasõnaga või Microsofti kontoga.",
                alertType: RHAlertBannerView.type.Error
            })
            this.view.setNeedsLayoutUpToRootView()
        }

        if (loginComponent?.parameters[LoginViewController.ParameterIdentifierName.entraOrganizationLoginRequired] === "YES") {
            this.statusBannerView.configureWithObject({
                text: "Selle e-posti aadressi organisatsioon kasutab RHR-is oma sisselogimist. " +
                    "Vajuta nuppu „Logi sisse oma organisatsiooni kontoga“.",
                alertType: RHAlertBannerView.type.Error
            })
            this.view.setNeedsLayoutUpToRootView()
        }

        if (loginComponent?.parameters[LoginViewController.ParameterIdentifierName.mfaChallenge] === "YES") {
            await this._loadAuthenticationChallenge()
        }
        
    }
    
    
    override updateViewConstraints() {
        
        super.updateViewConstraints()
        
    }
    
    
    override updateViewStyles() {
        
        super.updateViewStyles()
        
    }
    
    
    override viewDidLayoutSubviews() {
        
        super.viewDidLayoutSubviews()
        
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding

        // View bounds
        const bounds = this.view.contentBoundsWithInset(padding)
        const introContentWidth = IF(bounds.width < 900)(() =>
            bounds.width * 0.85
        ).ELSE(() =>
            [bounds.width * 0.8, 1125].min()
        )
        const introContentBounds = bounds.rectangleWithWidth(introContentWidth, 0.5)
        const authContentBounds = bounds.rectangleWithWidth(
            [bounds.width, RHLoginViewTokens.authContentMaxWidth].min(),
            0.5
        )
        // Welcome heading at the top (nudged down a little), then the beta-version notice, then the mission copy.
        this.welcomeHeadingLabel.frame = introContentBounds
            .rectangleWithY(introContentBounds.y + RHSpacing.xLarge)
            .rectangleWithHeight(
                [this.welcomeHeadingLabel.intrinsicContentHeight(introContentBounds.width), labelHeight * 2].max()
            )
        this.titleLabel.frame = this.welcomeHeadingLabel.frame.rectangleForNextRow(
            RHSpacing.medium,
            [this.titleLabel.intrinsicContentHeight(introContentBounds.width) + padding, labelHeight * 3].max()
        )
        this.explanationView.frame = this.titleLabel.frame.rectangleForNextRow(
            padding,
            [this.explanationView.intrinsicContentHeight(this.titleLabel.frame.width), padding].max()
        )

        // Global procurement search box (field + green "Otsi" button), spanning the same width as the intro copy.
        const searchRowFrame = introContentBounds
            .rectangleWithY(this.explanationView.frame.max.y + RHSpacing.xLarge)
            .rectangleWithHeight(44)
        searchRowFrame.distributeViewsAlongWidth(
            [this.searchField, this.searchButton],
            undefined,
            padding,
            [nil, 96]
        )
        this.browseProcurementsButton.frame = searchRowFrame.rectangleForNextRow(
            RHSpacing.small,
            24
        )

        const bannerHeight = IF(IS(this.statusBannerView.text))(() =>
            this.statusBannerView.intrinsicContentHeight(authContentBounds.width)
        ).ELSE(() => 0)
        this.statusBannerView.frame = authContentBounds
            .rectangleWithY(this.browseProcurementsButton.frame.max.y + RHSpacing.xxLarge)
            .rectangleWithHeight(bannerHeight)

        IF(this._authenticationChallengeKind)(() => {
            const challengeTitleHeight = [
                this.mfaTitleLabel.intrinsicContentHeight(authContentBounds.width),
                labelHeight * 2
            ].max()
            const challengeInstructionHeight = [
                this.mfaInstructionLabel.intrinsicContentHeight(authContentBounds.width),
                labelHeight * 3
            ].max()
            const challengeSecretHeight = IF(IS(this.mfaSecretLabel.text))(() =>
                [
                    this.mfaSecretLabel.intrinsicContentHeight(authContentBounds.width),
                    labelHeight * 2
                ].max()
            ).ELSE(() =>
                0
            )
            const challengeCodeHeight = [
                this.mfaCodeTextField.intrinsicContentHeight(authContentBounds.width),
                RHLoginViewTokens.authButtonHeight
            ].max()

            this.statusBannerView.frame
                .rectangleForNextRow(padding, challengeTitleHeight)
                .assignedAsFrameOfView(this.mfaTitleLabel)
                .rectangleForNextRow(RHSpacing.small, challengeInstructionHeight)
                .assignedAsFrameOfView(this.mfaInstructionLabel)
                .rectangleForNextRow(RHSpacing.medium, challengeSecretHeight)
                .assignedAsFrameOfView(this.mfaSecretLabel)
                .rectangleForNextRow(padding, challengeCodeHeight)
                .distributeViewsAlongWidth(
                    [this.mfaCodeTextField, this.verifyMfaButton],
                    undefined,
                    padding,
                    [nil, 144]
                )
                .rectangleForNextRow(RHSpacing.small, labelHeight * 1.5)
                .assignedAsFrameOfView(this.cancelMfaButton)
                .rectangleForNextRow(RHSpacing.large, 24)
                .assignedAsFrameOfView(this.statisticsLink)
                .rectangleForNextRow(padding, 1)
                .assignedAsFrameOfView(this.bottomView)
        }).ELSE(() => {
        // Email and password sit side by side as two narrower columns within the auth column.
        const authFieldHeight = [
            this.usernameTextField.intrinsicContentHeight(authContentBounds.width),
            this.passwordTextField.intrinsicContentHeight(authContentBounds.width),
            padding
        ].max()
        const authFieldsRowFrame = this.statusBannerView.frame.rectangleForNextRow(padding, authFieldHeight)
        authFieldsRowFrame.distributeViewsEquallyAlongWidth(
            [this.usernameTextField, this.passwordTextField],
            padding
        )

        this.forgotPasswordButton.frame = authFieldsRowFrame.rectangleForNextRow(
            padding / 2,
            labelHeight * 1.5
        )
        
        const authButtonRowFrame = this.forgotPasswordButton.frame.rectangleForNextRow(
            padding,
            RHLoginViewTokens.authButtonHeight
        )
        const loginButtonWidth =
            this.loginButton.titleLabel.intrinsicContentWidth(RHLoginViewTokens.authButtonHeight) +
            RHButtons.contentPadding * 2 +
            RHLoginViewTokens.authButtonTextSafetyBuffer
        const registerUserButtonWidth =
            this.registerUserButton.titleLabel.intrinsicContentWidth(RHLoginViewTokens.authButtonHeight) +
            RHButtons.contentPadding * 2 +
            RHLoginViewTokens.authButtonTextSafetyBuffer
        const authButtonRowWidth =
            loginButtonWidth +
            registerUserButtonWidth +
            RHSpacing.medium
        const centeredAuthButtonRowFrame = authButtonRowFrame.rectangleWithWidth(authButtonRowWidth, 0.5)
        
        this.loginButton.frame = centeredAuthButtonRowFrame.rectangleWithWidth(loginButtonWidth)
        this.registerUserButton.frame = this.loginButton.frame.rectangleForNextColumn(
            RHSpacing.medium,
            registerUserButtonWidth
        )
        
        const oauthButtonWidth = authContentBounds.width
        const oauthButtonHeight = RHLoginViewTokens.authButtonHeight
        
        centeredAuthButtonRowFrame
            .rectangleForNextRow(padding * 2, oauthButtonHeight)
            .rectangleWithWidth(oauthButtonWidth, 0.5)
            .assignedAsFrameOfView(this.googleLoginButton)
            .rectangleForNextRow(padding, oauthButtonHeight)
            .assignedAsFrameOfView(this.microsoftLoginButton)
            .IF(this.view.hasSubview(this.organizationLoginButton))
                .rectangleForNextRow(padding, oauthButtonHeight)
                .assignedAsFrameOfView(this.organizationLoginButton)
            .ENDIF()
            .rectangleForNextRow(RHSpacing.large, 24)
            .assignedAsFrameOfView(this.statisticsLink)
            .rectangleForNextRow(padding, 1)
            .assignedAsFrameOfView(this.bottomView)
        })
        
    }
    
}
